import { MongoServerError } from 'mongodb'

const MONGO_DOCUMENT_VALIDATION_ERROR_CODE = 121

/**
 * Represents details about a property that failed validation
 */
export type ValidationDetail = {
  /** The validation operator that failed (e.g., 'maxLength', 'minimum') */
  operatorName?: string
  /** The specification that was violated */
  specifiedAs?: Record<string, unknown>
  /** Human-readable reason for the validation failure */
  reason?: string
  /** The actual value that was rejected */
  consideredValue?: unknown
  /** The type of the value that was rejected */
  consideredType?: unknown
}

/**
 * Represents a property that failed MongoDB validation
 */
export type ValidationProperty = {
  /** Name of the property that failed validation */
  propertyName: string
  /** Details about why validation failed */
  details: ValidationDetail[]
}

/**
 * MongoDB validation error structure as returned by the server
 */
export type DocumentValidationError = {
  /** ID of the document that failed validation */
  failingDocumentId?: string
  /** Error details from MongoDB */
  details?: {
    /** The validation operator (usually '$jsonSchema') */
    operatorName?: string
    /** Rules that weren't satisfied by the document */
    schemaRulesNotSatisfied?: [
      {
        /** The specific rule operator that failed */
        operatorName: string
        /** Properties that didn't satisfy the validation rules */
        propertiesNotSatisfied?: readonly ValidationProperty[] | null
        /** Names of properties that were present but not allowed by the schema (for the `additionalProperties` rule) */
        additionalProperties?: readonly string[] | null
        /** The specification that was violated for this rule */
        specifiedAs?: Record<string, unknown>
      },
    ]
  }
}

type ValidationRule = NonNullable<NonNullable<DocumentValidationError['details']>['schemaRulesNotSatisfied']>[number]
type FieldErrorMap = Record<string, ValidationDetail[]>

/**
 * A simplified representation of MongoDB validation errors
 * Maps operator names to property details for easier client-side handling
 */
export type ValidationErrors = Record<string, Record<string, ValidationDetail[]>[]>[] | undefined

/**
 * Checks if an error is a MongoDB server error
 * @param error The error to check
 * @returns True if the error is a MongoDB server error
 */
export function isMongoServerError(error: unknown): error is MongoServerError {
  return error instanceof MongoServerError
}

/**
 * Transforms validation error properties into a more readable format
 * @param properties The properties that failed validation
 * @returns A mapped array of property errors with details
 * @internal
 */
const formatValidationProperties = (properties: readonly ValidationProperty[]) =>
  properties.map((prop) => ({ [prop.propertyName]: prop.details }))

const hasAdditionalProperties = (
  rule: ValidationRule,
): rule is ValidationRule & { additionalProperties: readonly string[] } =>
  rule.operatorName === 'additionalProperties' && (rule.additionalProperties ?? []).length > 0

const formatAdditionalPropertiesRule = (rule: ValidationRule & { additionalProperties: readonly string[] }) => {
  const detail: ValidationDetail = {
    operatorName: 'additionalProperties',
    specifiedAs: rule.specifiedAs,
    reason: 'property is not allowed by the schema',
  }
  const properties = rule.additionalProperties.map((propertyName) => ({
    propertyName,
    details: [detail],
  }))

  return { [rule.operatorName]: formatValidationProperties(properties) }
}

const formatValidationRule = (rule: ValidationRule) =>
  hasAdditionalProperties(rule)
    ? formatAdditionalPropertiesRule(rule)
    : { [rule.operatorName]: formatValidationProperties(rule.propertiesNotSatisfied ?? []) }

const getRuleProperties = (ruleSet: Record<string, FieldErrorMap[]>) => Object.values(ruleSet).flat()

/**
 * Attempts to extract validation error details from a MongoDB server error
 * @param error The error to process — may be any unknown value
 * @returns A simplified representation of validation errors or undefined if not a validation error
 */
export function extractValidationErrors(error: unknown): ValidationErrors | undefined {
  // Check if this is actually a MongoDB validation error
  if (!isMongoServerError(error) || error.code !== MONGO_DOCUMENT_VALIDATION_ERROR_CODE) {
    return undefined
  }

  // Safely extract schema rules not satisfied
  const schemaRulesNotSatisfied =
    (error.errInfo as DocumentValidationError | undefined)?.details?.schemaRulesNotSatisfied ?? []
  if (schemaRulesNotSatisfied.length === 0) {
    return undefined
  }

  return schemaRulesNotSatisfied.map(formatValidationRule)
}

/**
 * Error class that simplifies handling of MongoDB validation errors
 * Extracts and provides easy access to validation details
 */
export class MongoValidationError extends Error {
  /** The extracted validation rules that weren't satisfied */
  public validationErrors?: ValidationErrors

  /** Flag indicating whether this is a document validation error */
  public hasValidationFailures: boolean

  /**
   * Creates an instance from a MongoDB server error
   * @param mongoError The original MongoDB error
   */
  public constructor(mongoError: Readonly<MongoServerError>) {
    super(mongoError.message, { cause: mongoError })
    this.validationErrors = extractValidationErrors(mongoError)
    this.hasValidationFailures = this.validationErrors !== undefined
  }

  /**
   * Returns the validation errors as a JSON string
   * @returns A JSON string of the validation errors or undefined if not a validation error
   */
  public getValidationErrorsAsString(): string | undefined {
    return JSON.stringify(this.validationErrors)
  }

  /**
   * Finds validation errors for a specific field
   * @param fieldName The field name to find errors for
   * @returns Array of validation details for the field or undefined if none found
   */
  public getFieldErrors(fieldName: string): ValidationDetail[] | undefined {
    const fieldErrors = this.validationErrors?.flatMap(getRuleProperties).find((property) => fieldName in property)
    return fieldErrors?.[fieldName]
  }
}
